import React from 'react'
import { FullBlockLoader, CircleLoader } from '@deal/components'
import { UserAgentConsumer } from '#src/containers/UserAgent'
import { Experiment } from '@deal/experiment-js'
import { isMobileiOS } from '#src/services/mobile'
import { IdentityConsumer } from '#src/containers/Identity'
import { EngagementChannelCategoryAssetsComponent, DepartmentType } from '#src/generated/graphql'
import config from '#src/config'
import cookies from 'js-cookie'
import BrandCategoryModal from './BrandCategoryModal'
import CategoryModal from './CategoryModal'
import CategoryHeader from './CategoryHeader'
import EmbeddedLandingPage from './EmbeddedLandingPage'
import EngagementModal from './EngagementModal'
import FixedBar from './FixedBar'
import FullPageTakeover from './FullPageTakeover'
import SMSModal from './SMSModal'
import IOSBanner from './IOSBanner'
import IOSFullPageTakeover from './IOSFullPageTakeover'

type ExperimentVariation =
  | 'category-header'
  | 'brand-category-modal'
  | 'category-modal-dual-headline'
  | 'category-modal-expert-count'
  | 'embedded-landing-page'
  | 'engagement-modal'
  | 'full-page-takeover'
  | 'fixed-bar'
  | 'banner'
  | 'sms-modal'

type iOSExperiment = {
  id: string
  variations: ExperimentVariation[]
  fallThrough: boolean
}

interface iOSExperimentProps {
  nextExperiment: () => void
}

export type ExperimentPlacements = 'product' | 'search' | 'experience'

type DefaultExperiment = {
  placement: ExperimentPlacements
  variations: ExperimentVariation[]
}

interface DefaultExperimentProps {
  categoryId: string
  sellableId?: string
}

interface EngagementChannelHandlerProps {
  categoryId: string
  sellableId?: string
  defaultExperiment?: DefaultExperiment
  iosExperiment?: iOSExperiment
}

interface ExperimentWrapperProps {
  allowLoggedIn?: boolean
  landingPageKey: string
}

const ExperimentWrapper: React.SFC<ExperimentWrapperProps> = ({
  children,
  allowLoggedIn,
  landingPageKey
}) => {
  return (
    <IdentityConsumer>
      {({ myself }) => {
        if (myself && !allowLoggedIn) {
          return null
        }

        // We would only ever set this for guest users
        if (!myself) {
          // Example cookie value: full-page-takeover-winter-sports-product
          cookies.set('curated_landing_page_key', landingPageKey, {
            domain: config.get('consumer.clientSideCookieDomain')
          })
        }

        return children
      }}
    </IdentityConsumer>
  )
}

const DefaultExperiment: React.SFC<DefaultExperiment & DefaultExperimentProps> = ({
  placement,
  variations,
  categoryId,
  sellableId
}) => {
  return (
    <IdentityConsumer>
      {({ myself }) => {
        return (
          <EngagementChannelCategoryAssetsComponent
            variables={{
              categoryId: categoryId
            }}
          >
            {({ data, loading: enagementChannelLoading }) => {
              if (enagementChannelLoading) {
                return <FullBlockLoader loader={CircleLoader} />
              } else if (data && data.category) {
                /**
                 * We can't launch any of the engagement channel treatments if the department isn't launched or accepting new requests
                 */
                if (
                  data.category.department &&
                  (!data.category.department.acceptingRequests ||
                    !data.category.department.launched)
                ) {
                  return null
                }

                return (
                  <Experiment
                    id={`engagement-channel-${placement}`}
                    default="full-page-takeover"
                    options={{
                      suppressTracking: myself !== undefined
                    }}
                  >
                    {({ result }) => {
                      if (result && variations.includes(result as ExperimentVariation)) {
                        const landingPageKey = `${result}-${data.category.slug}-${placement}`

                        switch (result) {
                          case 'category-header':
                            /**
                             * This is currently for golf only, handled on the search placement but also checked here
                             */
                            if (
                              data.category.department &&
                              data.category.department.type !== DepartmentType.GOLF
                            ) {
                              return null
                            }

                            return (
                              <ExperimentWrapper landingPageKey={landingPageKey}>
                                <CategoryHeader categorySlug={data.category.slug} />
                              </ExperimentWrapper>
                            )

                          case 'sms-modal':
                            return (
                              <ExperimentWrapper
                                allowLoggedIn={true}
                                landingPageKey={landingPageKey}
                              >
                                <SMSModal categoryId={data.category.id} sellableId={sellableId} />
                              </ExperimentWrapper>
                            )

                          case 'engagement-modal':
                            return (
                              <ExperimentWrapper landingPageKey={landingPageKey}>
                                <EngagementModal category={data.category} />
                              </ExperimentWrapper>
                            )

                          case 'brand-category-modal':
                            return (
                              <ExperimentWrapper landingPageKey={landingPageKey}>
                                <BrandCategoryModal sellableId={sellableId} />
                              </ExperimentWrapper>
                            )

                          case 'embedded-landing-page':
                            return (
                              <ExperimentWrapper landingPageKey={landingPageKey}>
                                <EmbeddedLandingPage sellableId={sellableId} />
                              </ExperimentWrapper>
                            )

                          case 'category-modal-dual-headline':
                            return (
                              <ExperimentWrapper landingPageKey={landingPageKey}>
                                <CategoryModal variation="v1" sellableId={sellableId} />
                              </ExperimentWrapper>
                            )

                          case 'category-modal-expert-count':
                            return (
                              <ExperimentWrapper landingPageKey={landingPageKey}>
                                <CategoryModal variation="v2" sellableId={sellableId} />
                              </ExperimentWrapper>
                            )

                          case 'full-page-takeover':
                            return (
                              <ExperimentWrapper landingPageKey={landingPageKey}>
                                <FullPageTakeover category={data.category} placement={placement} />
                              </ExperimentWrapper>
                            )

                          case 'fixed-bar':
                            return (
                              <ExperimentWrapper landingPageKey={landingPageKey}>
                                <FixedBar category={data.category} />
                              </ExperimentWrapper>
                            )
                        }
                      }

                      return null
                    }}
                  </Experiment>
                )
              } else {
                return null
              }
            }}
          </EngagementChannelCategoryAssetsComponent>
        )
      }}
    </IdentityConsumer>
  )
}

const IOSExperiment: React.SFC<iOSExperiment & iOSExperimentProps> = ({
  id,
  variations,
  fallThrough,
  nextExperiment
}) => {
  return (
    <Experiment id={id} default="banner">
      {({ result }) => {
        let ResultComponent: React.SFC<{}> | undefined

        if (result && variations.includes(result as ExperimentVariation)) {
          switch (result) {
            case 'banner':
              ResultComponent = () => <IOSBanner />
              break

            case 'full-page-takeover':
              ResultComponent = () => <IOSFullPageTakeover />
          }
        }

        if (!fallThrough && !ResultComponent) {
          return null
        } else if (ResultComponent) {
          return <ResultComponent />
        } else {
          return nextExperiment()
        }
      }}
    </Experiment>
  )
}

const EngagementChannelHandler: React.SFC<EngagementChannelHandlerProps> = ({
  iosExperiment,
  defaultExperiment,
  categoryId,
  sellableId
}) => {
  return (
    <UserAgentConsumer>
      {userAgent => {
        const isUserMobileiOS = isMobileiOS(userAgent)

        /**
         * Check if there is an iOS experiment passed in, if there is
         * and ther user is eligible, and the appropriate variations
         * are allowed, then decide on the users cohort.
         */
        if (iosExperiment && isUserMobileiOS) {
          return (
            <IOSExperiment
              nextExperiment={() =>
                defaultExperiment ? (
                  <DefaultExperiment
                    {...defaultExperiment}
                    categoryId={categoryId}
                    sellableId={sellableId}
                  />
                ) : null
              }
              {...iosExperiment}
            />
          )
        }

        /**
         * There is a case where we render just the default experiment or there
         * is nothing passed in at all and we just return null. The separation of these
         * will let us insert an 'android' experiment or generic mobile experiment with little
         * effort.
         */
        if (defaultExperiment) {
          return (
            <DefaultExperiment
              {...defaultExperiment}
              categoryId={categoryId}
              sellableId={sellableId}
            />
          )
        } else {
          return null
        }
      }}
    </UserAgentConsumer>
  )
}

export default EngagementChannelHandler
